'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Testimonial data
const testimonials = [
  {
    content:
      "Sketchplay has provided a game changing experience for my organization. We dreaded having multiple apps for different reason and Sketchplay has eliminated that headache. Everything is one place and allows us to focus on what really matters and that's our athletes and their families. Building a program without Sketchplay seems like nonsense now. If you want to have a successful organization, it starts with Sketchplay.",
    author: "Brandon Taylor",
    role: "Head Coach",
    organization: "Nashville Savages Football Program",
    avatar: "/images/testimonials/7v7savages.png",
    color: "blue",
    gradient: "from-futuristic-blue via-athletic-blue to-athletic-indigo",
    glow: "shadow-blue-glow",
  },
  {
    content:
      "As a coach, I can finally focus on coaching instead of administration. The training tools help me plan sessions, track player development, and share video analysis. It's been a game-changer for our program.",
    author: "Michael Rodriguez",
    role: "Head Coach",
    organization: "Elite Basketball Academy",
    avatar: "/placeholder-avatar.jpg",
    color: "indigo",
    gradient: "from-athletic-indigo via-athletic-purple to-futuristic-purple",
    glow: "shadow-purple-glow",
  },
  {
    content:
      "Running tournaments used to be a logistical nightmare. With Sketchplay's competition tools, we can manage registrations, scheduling, and results all in one platform. Our participants love the real-time updates.",
    author: "Jennifer Lee",
    role: "Tournament Director",
    organization: "National Volleyball Association",
    avatar: "/placeholder-avatar.jpg",
    color: "purple",
    gradient: "from-athletic-purple via-futuristic-purple to-futuristic-neon",
    glow: "shadow-neon-glow",
  },
  {
    content:
      "As a parent of three athletes in different sports, Sketchplay has simplified my life tremendously. I can track all their schedules, communicate with coaches, and handle payments through one app.",
    author: "David Thompson",
    role: "Parent",
    organization: "Multi-Sport Family",
    avatar: "/placeholder-avatar.jpg",
    color: "violet",
    gradient: "from-futuristic-purple via-athletic-violet to-futuristic-neon",
    glow: "shadow-purple-glow",
  },
];

// Animation variants
const titleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.5,
      delay: 0.1
    },
  },
};

const subtitleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.5,
      delay: 0.2
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.6
    }
  },
  exit: { 
    opacity: 0, 
    y: -40,
    transition: { 
      duration: 0.3
    }
  }
};

const buttonVariants = {
  rest: { scale: 1 },
  hover: { 
    scale: 1.1,
    transition: { 
      type: "spring", 
      stiffness: 400, 
      damping: 10 
    }
  },
  tap: { scale: 0.95 }
};

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  
  // Autoplay functionality
  useEffect(() => {
    setIsMounted(true);
    
    if (!isAutoplay) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000);
    
    return () => clearInterval(interval);
  }, [isAutoplay]);
  
  // Pause/resume autoplay
  const pauseAutoplay = () => setIsAutoplay(false);
  const resumeAutoplay = () => setIsAutoplay(true);
  
  // Navigation
  const goToTestimonial = (index: number) => {
    setActiveIndex(index);
  };
  
  const goToPrev = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };
  
  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section className="relative py-24 overflow-hidden bg-futuristic-black">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-blue/10 via-futuristic-purple/5 to-transparent opacity-70"></div>
        
        {/* Grid pattern removed as requested */}
      </div>
      
      {/* Animated particles */}
      {isMounted && (
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute top-1/4 right-1/4 w-1 h-1 rounded-full bg-white opacity-80"
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: "loop",
            }}
          />
          <motion.div
            className="absolute top-2/3 right-1/3 w-2 h-2 rounded-full bg-athletic-blue opacity-60"
            animate={{
              y: [0, -150, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "loop",
              delay: 1,
            }}
          />
          <motion.div
            className="absolute top-1/3 right-2/3 w-1.5 h-1.5 rounded-full bg-athletic-purple opacity-70"
            animate={{
              y: [0, -120, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              repeatType: "loop",
              delay: 2,
            }}
          />
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-transparent"
            variants={titleVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            Trusted by sports organizations across the US and Canada.
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-300"
            variants={subtitleVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            See what our customers have to say about Sketchplay
          </motion.p>
        </div>

        <div 
          className="max-w-4xl mx-auto"
          onMouseEnter={pauseAutoplay}
          onMouseLeave={resumeAutoplay}
        >
          <div className="relative min-h-[400px]">
            {/* Testimonial Cards */}
            <AnimatePresence mode="wait">
              {testimonials.map((testimonial, index) => (
                index === activeIndex && (
                  <motion.div
                    key={index}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute inset-0"
                  >
                    <div className="relative h-full">
                      {/* Background glow */}
                      <div className={`absolute inset-0 rounded-2xl ${testimonial.glow} opacity-10 blur-xl -z-10`} />
                      
                      {/* Card */}
                      <div className="bg-futuristic-dark/80 backdrop-blur-sm rounded-2xl border border-white/5 hover:border-white/10 transition-all duration-500 p-8 md:p-10 h-full">
                        {/* Gradient top border */}
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${testimonial.gradient} rounded-t-2xl`} />
                        
                        {/* Quote Icon */}
                        <div className={`mb-6 bg-gradient-to-r ${testimonial.gradient} bg-clip-text text-transparent`}>
                          <svg
                            className="h-12 w-12 opacity-80"
                            fill="currentColor"
                            viewBox="0 0 32 32"
                            aria-hidden="true"
                          >
                            <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                          </svg>
                        </div>
                        
                        {/* Testimonial Content */}
                        <p className="text-xl md:text-2xl font-medium text-white mb-8 leading-relaxed">
                          "{testimonial.content}"
                        </p>
                        
                        {/* Author Info */}
                        <div className="flex items-center">
                          <div className={`h-14 w-14 rounded-full p-0.5 bg-gradient-to-br ${testimonial.gradient} ${testimonial.glow}`}>
                            <div className="bg-futuristic-black/90 rounded-full w-full h-full flex items-center justify-center text-white text-xl font-bold">
                              {testimonial.author.charAt(0)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-lg font-medium text-white">{testimonial.author}</div>
                            <div className="text-base text-gray-400">
                              {testimonial.role}, <span className="text-gray-300">{testimonial.organization}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              ))}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between pointer-events-none -mx-6">
              <motion.button
                variants={buttonVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
                onClick={goToPrev}
                className="w-12 h-12 rounded-full bg-futuristic-dark/80 backdrop-blur-sm shadow-lg flex items-center justify-center text-white border border-white/10 hover:border-white/20 transition-colors pointer-events-auto focus:outline-none group"
                aria-label="Previous testimonial"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 group-hover:text-futuristic-neon transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </motion.button>
              <motion.button
                variants={buttonVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
                onClick={goToNext}
                className="w-12 h-12 rounded-full bg-futuristic-dark/80 backdrop-blur-sm shadow-lg flex items-center justify-center text-white border border-white/10 hover:border-white/20 transition-colors pointer-events-auto focus:outline-none group"
                aria-label="Next testimonial"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 group-hover:text-futuristic-neon transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </motion.button>
            </div>
          </div>

          {/* Indicators */}
          <div className="flex justify-center mt-8 space-x-3">
            {testimonials.map((testimonial, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className="group focus:outline-none"
                aria-label={`Go to testimonial ${index + 1}`}
              >
                <div className={`w-10 h-1.5 rounded-full transition-all duration-300 ${
                  index === activeIndex 
                    ? `bg-gradient-to-r ${testimonial.gradient} ${testimonial.glow} scale-100` 
                    : 'bg-white/20 scale-75 group-hover:bg-white/30'
                }`} 
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
} 